/*
What a game mode is called, and the two numbers survival's lobby quotes.

A mode is a string the server publishes (`game_modes` in `GET /game-config/`), and until survival
there was nothing to translate: "1v1", "2v2" and "3v3" are the same three characters in every
language, so the lobby printed the raw string and the dialogs listed the three literally. Survival
is a *word*, so it needs wording — and one helper rather than a `mode === "survival" ? … : mode`
at each of the three places that print one, because the fourth place is where the two drift.

The wave arithmetic here mirrors `BaseSurvivalConfig` (SV6) and reads every number off the
published block: the lobby says how many enemies a game will open with and how many each wave
brings, and a number on screen is a claim about what the server will do. With no config in hand
these answer `undefined` and the line that quotes them does not render — deliberately, rather than
falling back to a copy of the defaults, which is exactly the second copy the rule is about.
*/

import i18n from "src/i18n"

export const SURVIVAL = "survival"

export const isSurvival = (mode) => mode === SURVIVAL

// Only survival is worded; everything else is its own name in every language.
export const gameModeName = (mode) => (isSurvival(mode) ? i18n.t("lobbyModals.modeSurvival") : mode)

// In survival the people hold one side and the bots the other, so "the enemy" is a side rather
// than a roster of seats.
export const otherTeam = (team) => (team === "human" ? "zombie" : team === "zombie" ? "human" : null)

// `max(1, ceil(people × share))` — the server's own sizing, in `games/survival.py`. A wave always
// brings at least one, which is why one person still meets a wave every cadence.
const botsFor = (people, share) =>
  typeof share === "number" ? Math.max(1, Math.ceil(people * share)) : undefined

// What the board opens with: one bot per person by default (wave 0).
export const survivalOpeningBots = (people, survival) =>
  botsFor(people, survival?.INITIAL_BOTS_PER_PLAYER)

// And what every wave after it brings.
export const survivalWaveBots = (people, survival) => botsFor(people, survival?.WAVE_BOTS_PER_PLAYER)

/* How many seats a mode has, and how few will start it (SV2) — the server's `game_mode_rules`
   table, with the screen's old arithmetic as the fallback.

   One row instead of `isSurvival(mode) ? ... : ...` wherever seats are counted: survival is only
   the first mode that is not two equal sides, and a second one would otherwise mean patching every
   one of those branches again. The fallback is for a bundle talking to a server from before the
   table was published — it reproduces exactly what this screen used to compute, and answers null
   when it cannot, so "complete" is never guessed.

   `min_seats_per_side` is the floor a *side* needs (A1, 2026-09-20): one person on each side of a
   classic room, so 1v2 and 1v3 are games the host may start. `min_seats` is still published
   beside it — `sides x min_seats_per_side` — because that is the name this row already had. */
export const seatRulesFor = (mode, published, { membersPerTeam, survival } = {}) => {
  const row = published?.[mode]
  // A server from before A1 publishes the row without the per-side floor, and that server still
  // refuses an uneven start — so the floor is derived from what it did publish rather than set to
  // 1, or the button would light up for a start the server is about to turn down.
  if (row) {
    return row.min_seats_per_side != null
      ? row
      : { ...row, min_seats_per_side: Math.ceil(row.min_seats / row.sides) }
  }
  if (isSurvival(mode)) {
    if (!survival?.MIN_PLAYERS) return null
    return {
      sides: 1,
      seats_per_side: membersPerTeam || survival.MAX_PLAYERS,
      min_seats_per_side: survival.MIN_PLAYERS,
      min_seats: survival.MIN_PLAYERS,
    }
  }
  return membersPerTeam
    ? { sides: 2, seats_per_side: membersPerTeam, min_seats_per_side: 1, min_seats: 2 }
    : null
}
